import {flow, getEnv, types} from "mobx-state-tree";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import {CodeTypeSelectState} from "../../elements/codeTypes/CodeTypeSelectState";
import CodeType from "../../../models/CodeType";
import {DimensionSelectViewState} from "../../elements/dimensions/dimensionSelectViewState";
import {codeBehavior} from "../../../utils/constants";
import Notification from "../../../utils/Notification";
import {getRuleTypes} from "../../../utils/common";
import RuleTypeStore from "../../../stores/domain/RuleTypeStore";
import {buildSchema} from "./simulateUiUtils";

export const SimulateCodeState = types.model('SimulateCodeState', {
  name: types.optional(types.string, ""),
  uuid: types.optional(types.string, ""),
  status: types.optional(types.string, ""),
  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  groupSelectStore: types.maybe(types.late(() => types.reference(GroupSelectState))),
  codeTypeSelectStore: types.maybe(types.late(() => types.reference(CodeTypeSelectState))),
  ruleTypeStore: types.maybe(types.late(() => types.reference(RuleTypeStore))),
  experienceSelected: types.maybe(types.reference(CodeType)),
  behavior: types.optional(types.string, ""),
  selectedDefaultUrl: types.optional(types.string, ''),
  dimensionSelectViewState: types.maybe(types.late(() => types.reference(DimensionSelectViewState))),
  primaryLandingUrl: types.optional(types.string, ''),
  simulatedLandingUrl: types.optional(types.string, ''),
  ruleTypes: types.optional(types.string, ''),
  scanFacts: types.optional(types.string, '{}'),
  simulateSchema: types.optional(types.string, '{"type": "object", "properties": {}}'),
  selectedCountryCode: types.optional(types.string, ""),
}).volatile(self => ({
  selectedAdditionalParams: [],
}))
  .actions(self => ({
    resetScanFacts() {
      self.scanFacts = "{}"
    },
    getSchema() {
      return JSON.parse(self.simulateSchema)
    },
    setSchema(obj) {
      self.simulateSchema = JSON.stringify(obj)
    },
    resetSchema() {
      self.simulateSchema = '{"type": "object", "properties": {}}';
      self.initSchema();
    },
    resetSelectedAdditionalParams() {
      self.selectedAdditionalParams = []
    },
    initSchema() {
      try {
        const simulateSchema = self.getSchema()
        const language = navigator.language || "en-US"; // some are only language, some are language-country
        const parts = language.split('-');
        const defaultLangCode = parts[0] || "en";
        const defaultCountryCode = parts[1] || "US";
        const defaultDevice = getDeviceType();
        simulateSchema.properties["country"] = buildSchema(self.ruleTypeStore, "country", defaultCountryCode, "select");
        simulateSchema.properties["lang"] = buildSchema(self.ruleTypeStore, "lang", defaultLangCode, "select");
        simulateSchema.properties["device"] = buildSchema(self.ruleTypeStore, "device", defaultDevice, "select");
        simulateSchema.properties["scanDate"] = buildSchema(self.ruleTypeStore, "scanDate", "", "dateTime", "Date Time");
        self.setSchema(simulateSchema)
        self.selectedCountryCode = defaultCountryCode
      } catch (error) {
        console.error('Error initSchema:', error);
      }
    },
    addAdditionalParamsToSchema(selected) {
      const removedOptions = self.selectedAdditionalParams.filter(
        (param) => !selected.some((sel) => sel.value === param.value)
      );
      self.selectedAdditionalParams = selected
      const simulateSchema = self.getSchema()
      selected.forEach((option) => {
        const {label, value, defaultValue, widget} = option;
        if (simulateSchema.properties[value]) return
        simulateSchema.properties[value] = buildSchema(self.ruleTypeStore, value, defaultValue, widget, label, self.selectedCountryCode);
      })
      removedOptions.forEach(({value}) => {
        delete simulateSchema.properties[value]
        const scanFacts = JSON.parse(self.scanFacts)
        delete scanFacts[value]
        self.scanFacts = JSON.stringify(scanFacts)
      })
      self.setSchema(simulateSchema)
    },
    updateScanFacts(data) {
      if (data.country !== self.selectedCountryCode) {
        self.selectedCountryCode = data.country
        const simulateSchema = self.getSchema()
        if (simulateSchema.properties["city"]) {
          simulateSchema.properties["city"] = buildSchema(self.ruleTypeStore, "city", "", "select", "City", self.selectedCountryCode);
        }
        if (simulateSchema.properties["countryRegion"]) {
          simulateSchema.properties["countryRegion"] = buildSchema(self.ruleTypeStore, "countryRegion", "", "select", "State/Province", self.selectedCountryCode);
        }
        self.setSchema(simulateSchema)
      }
      self.scanFacts = JSON.stringify(data)
    },
    hydrateForm(code) {
      self.name = code?.name
      self.experienceSelected = code?.type
      self.codeTypeSelectStore.setDefaultCodeType(code?.type)
      if (code?.behavior === codeBehavior.STATIC) {
        self.selectedDefaultUrl = code?.shortUrl?.uri
      } else {
        self.selectedDefaultUrl = code?.shortUrl?.domain + "/" + code?.shortUrl?.uri
      }
      self.behavior = code?.behavior
      self.groupSelectStore.selectGroup(code?.owner?.groupUuid)
      self.tagSelectStore.setSelectedTags(code?.tags?.flatMap((tag) => tag.uuid))
      self.codeStatus = code?.status
      self.ruleTypes = getRuleTypes(code.rules).join(", ")
      self.primaryLandingUrl = code?.data?.url || code?.dataDetail?.secondaryExperience?.data?.url
      if (code?.owner?.dimensions) {
        self.dimensionSelectViewState.setDimensions(code?.owner?.dimensions)
      }
    },
    fetchWithID: flow(function* f() {
      if (self.uuid === "") return
      self.isLoading = true
      const manager = getEnv(self).codeManager
      const res = yield manager.fetchQrcodeWithId(self.uuid)
      self.hydrateForm(res)
      self.isLoading = false
      return res
    }),
    evaluateCode: flow(function* f(excludeUtmAndParams = false) {
      const manager = getEnv(self).codeManager
      self.simulatedLandingUrl = yield manager.evaluateCode(self, excludeUtmAndParams)
      return self.simulatedLandingUrl
    }),
    copyPrimaryLandingUrl() {
      navigator.clipboard.writeText(self.primaryLandingUrl).then(() => {
        new Notification("success", "'" + self.primaryLandingUrl + "' was copied to your clipboard.").send();
      });
    },
    copySimulatedLandingUrl() {
      navigator.clipboard.writeText(self.simulatedLandingUrl).then(() => {
        new Notification("success", "'" + self.simulatedLandingUrl + "' was copied to your clipboard.").send();
      });
    },
  })).views((self) => ({
    shouldRenderURL() {
      return self.behavior !== codeBehavior.STATIC
    },
    scanFactsView() {
      return JSON.parse(self.scanFacts)
    },
    additionalParamsOptions() {
      return [
        {label: "City", value: "city", defaultValue: "", widget: "select"},
        {label: "MetroCode/DMA", value: "metroCode", defaultValue: "500", widget: "select"},
        {label: "Postal /Zip Code", value: "postalCode", defaultValue: "", widget: "string"},
        {label: "State/Province", value: "countryRegion", defaultValue: "", widget: "select"},
        {label: "Scans per Code", value: "globalScanCount", defaultValue: "", widget: "string"},
        {label: "Scans per Code/User", value: "userScanCount", defaultValue: "", widget: "string"},
      ]
    }
  }))


function getDeviceType() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "IOS";
  } else if (/Android/i.test(userAgent)) {
    return "ANDROID";
  } else if (/Tablet|iPad/i.test(userAgent)) {
    return "TABLET";
  } else if (/SMART-TV|SmartTV|NetCast|Tizen|WebOS|TV/i.test(userAgent)) {
    return "SMARTTV";
  } else if (/Mobi|Mobile/i.test(userAgent)) {
    return "MOBILE";
  } else {
    return "DESKTOP";
  }
}
